import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css, useTheme } from 'styled-components';
import { compose } from 'recompose';
import { connect } from 'react-redux';

import { EXAM_MODE, QUIZ_VARIANT } from 'Util/constants';
import { navigateToTungKai } from 'Util/navigateToTungKai';
import { canRedirectToTungKai } from 'Routes/MyCourses/Components/utils.js';
import { useConfig } from 'Context/ConfigContext';
import { getTextColorByContrast } from 'Util/color';
import { useClassroomNavigation } from 'Util/Hooks';
import IconComponent from 'Routes/Classroom/Components/IconComponent';
import { useOffset } from 'Context/LayoutContext/OffsetProvider';

import useSubsectionContent from '../../../../Routes/Classroom/Domain/useSubsectionContent';
import { useClassroomContext } from '../../../../Context/ClassroomContext';
import { myProfile } from '../../../../Redux/user-profile/selectors';
import GamificationModal from '../../../../Routes/Classroom/Components/GamificationModal';
import { Box, ButtonFilled, Flex, Link, Image, SvgIcon, Text } from '../../../Base';
import { withCapacitor } from '../../../../Util/Capacitors/hoc';

const ScoreBox = styled(Flex)`
  height: 240px;
  padding: 32px;
  min-width: 234px;
  border-radius: 44px;
`;
ScoreBox.defaultProps = {
  bg: 'white',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  mb: 7,
};

const DataRow = ({ fieldName, value }) => {
  return (
    <Flex mb={4}>
      <Text style={{ flexBasis: '50%' }}>{fieldName}</Text>
      <Text textStyle={'body1Medium'} style={{ flexBasis: '50%' }}>
        {value}
      </Text>
    </Flex>
  );
};

const WhiteButton = styled((props) => (
  <ButtonFilled
    minWidth={'max-content'}
    width={145}
    height={40}
    borderRadius={20}
    color={'white'}
    textColor={'text'}
    fontWeight={600}
    {...props}
  />
))`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 1 50%;
`;

const DoAgainButton = styled((props) => (
  <ButtonFilled
    width={1}
    maxWidth={370}
    height={40}
    borderRadius={20}
    colors={'primary'}
    fontWeight={600}
    m={'auto'}
    mt={60}
    {...props}
  />
))`
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ textColor }) => css`
    color: ${textColor};
    &:hover {
      color: ${textColor};
    }
  `}
`;

const Result = ({
  score,
  fullScore,
  onSeeMoreScore,
  onSeeSolution,
  onGoToNextSubsection,
  onDoQuizAgain,
  quizTitle,
  round,
  averageScore,
  disableDoQuizAgainButton,
  capacitorContext,
  user,
}) => {
  const {
    classroom: { quiz = {} },
    tungKai,
    userApi,
  } = useConfig();
  const { openInApp } = capacitorContext;

  const { bgColor = 'primary', gradedExam = {}, imagePrefix = '', imageSuffix = 'svg' } = quiz;
  const { showSolution = false } = gradedExam;
  const { colors } = useTheme();
  const textColor = getTextColorByContrast(colors.primary, colors.text, colors.white);
  const dataTestIdScorePrefix = 'quiz-score';
  const { nextSubsection } = useClassroomNavigation();
  const { topOffset } = useOffset();
  const { classroomCourseId, courseId } = useClassroomContext();

  const { data: subsectionContentData } = useSubsectionContent({
    variables: {
      classroomCourseId,
      subSectionId: nextSubsection?.id,
      subSectionReferId: nextSubsection?.referId,
    },
    skip: !nextSubsection,
    fetchPolicy: 'network-only',
  });

  const examMode = useMemo(
    () =>
      subsectionContentData?.classroomSubSectionContent?.assets?.contentAssets?.[0]?.content
        ?.examMode,
    [subsectionContentData]
  );
  const isQuizFeed = nextSubsection?.contentType === 'QUIZ_FEED';

  const { enable, enableWhiteList, whitelist, quizFooterRedirectPath } = tungKai || {};

  const canGoToTungKai = canRedirectToTungKai({
    enable,
    enableWhiteList,
    whitelist,
    userId: user?.id,
  });

  const handleGoToNextSubsection = () => {
    if ((examMode === EXAM_MODE.RANDOM || isQuizFeed) && canGoToTungKai) {
      const variant = isQuizFeed ? QUIZ_VARIANT.QUIZ_FEED : QUIZ_VARIANT.QUIZ_V2;
      const redirectPath = (quizFooterRedirectPath || '/')
        .replace('{courseId}', courseId)
        .replace('{subsectionId}', nextSubsection.id)
        .replace('{variant}', variant);

      return navigateToTungKai({
        redirectPath,
        config: { userApi, tungKai },
        openInApp,
      });
    }

    onGoToNextSubsection();
  };

  return (
    <Box>
      <Flex bg={bgColor} pt={topOffset > 0 ? topOffset : 3}>
        <IconComponent
          iconComponent={<SvgIcon icon={SvgIcon.ICON_LIST.ARROW_LEFT} color={'white'} size={24} />}
          onClick={onSeeMoreScore}
        />
      </Flex>
      <Flex
        data-testid={`${dataTestIdScorePrefix}`}
        flexDirection={'column'}
        bg={bgColor}
        pb={8}
        alignItems={'center'}
      >
        <Text
          textStyle={'heading4'}
          data-testid={`${dataTestIdScorePrefix}-header`}
          color={'white'}
          mb={7}
        >
          คุณได้คะแนน
        </Text>
        <ScoreBox>
          <Image src={`/assets/images/quiz/${imagePrefix}quiz-score.${imageSuffix}`} mb={5} />
          <Text
            textStyle={'heading1'}
            data-testid={`${dataTestIdScorePrefix}-number`}
            fontWeight={'bold'}
            mb={3}
          >
            {score}/{fullScore}
          </Text>
          <Link
            data-testid={`${dataTestIdScorePrefix}-other-round`}
            onClick={onSeeMoreScore}
            style={{ textDecoration: 'underline' }}
          >
            ดูคะแนนรอบอื่น
          </Link>
        </ScoreBox>
        <Flex width={'100%'} maxWidth={1164} justifyContent={'space-around'} px={6}>
          {showSolution && (
            <WhiteButton onClick={onSeeSolution} mr={6}>
              ดูเฉลย
            </WhiteButton>
          )}
          {nextSubsection && (
            <WhiteButton
              data-testid={`btn-${dataTestIdScorePrefix}-next-lesson`}
              onClick={handleGoToNextSubsection}
            >
              เรียนบทต่อไป
              <SvgIcon icon={SvgIcon.ICON_LIST.NEXT} size={24} />
            </WhiteButton>
          )}
        </Flex>
      </Flex>

      <Flex
        flexDirection={'column'}
        justifyContent={'center'}
        px={6}
        pt={5}
        m={'auto'}
        width={'100%'}
        maxWidth={1164}
      >
        <DataRow
          data-testid={`${dataTestIdScorePrefix}-title`}
          fieldName={'แบบฝึกหัด'}
          value={quizTitle}
        />
        <DataRow
          data-testid={`${dataTestIdScorePrefix}-nth-round`}
          fieldName={'รอบที่'}
          value={round}
        />
        <DataRow
          data-testid={`${dataTestIdScorePrefix}-average`}
          fieldName={'คะแนนเฉลี่ยของคุณ'}
          value={averageScore}
        />
        <DoAgainButton
          data-testid={`btn-${dataTestIdScorePrefix}-redo`}
          onClick={onDoQuizAgain}
          disabled={disableDoQuizAgainButton}
          textColor={textColor}
        >
          ทำแบบฝึกหัดอีกครั้ง
        </DoAgainButton>
      </Flex>
      <GamificationModal />
    </Box>
  );
};

Result.propTypes = {
  score: PropTypes.number,
  fullScore: PropTypes.number,
  onSeeMoreScore: PropTypes.func,
  onSeeSolution: PropTypes.func,
  onGoToNextSubsection: PropTypes.func,
  onDoQuizAgain: PropTypes.func,
  quizTitle: PropTypes.string,
  round: PropTypes.number,
  averageScore: PropTypes.string,
  disableDoQuizAgainButton: PropTypes.bool,
  disableSeeSolution: PropTypes.bool,
  nextSubsection: PropTypes.object,
};

export default compose(
  withCapacitor,
  connect((state) => ({
    user: myProfile(state) ?? {},
  }))
)(Result);
